// src/pages/Admins/components/AdminFormModal.tsx
import React from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {X, Shield, Mail, Phone, Crown, AlertTriangle} from "lucide-react";
import {useToast} from "../../../hooks/common/useToast";
import {
  useUpdateAdminMutation,
  useCreateAdminMutation,
} from "../../../store/api/adminsApi";
import type {UserResponseDto} from "../../../types/users";

// Validation schema for admin form
const adminSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  isVerified: z.boolean().optional(),
});

type AdminFormData = z.infer<typeof adminSchema>;

interface AdminFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: UserResponseDto | null;
  isEditMode?: boolean;
}

const AdminFormModal: React.FC<AdminFormModalProps> = ({
  isOpen,
  onClose,
  admin,
  isEditMode = false,
}) => {
  const {success, error} = useToast();
  const [updateAdmin, {isLoading: isUpdating}] = useUpdateAdminMutation();
  const [createAdmin, {isLoading: isCreating}] = useCreateAdminMutation();

  const isLoading = isUpdating || isCreating;

  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting},
  } = useForm<AdminFormData>({
    resolver: zodResolver(adminSchema),
    defaultValues: {
      name: admin?.name || "",
      email: admin?.email || "",
      phone: admin?.phone || "",
      isVerified: admin?.isVerified || false,
    },
  });

  // Reset form when admin changes
  React.useEffect(() => {
    if (admin && isEditMode) {
      reset({
        name: admin.name,
        email: admin.email,
        phone: admin.phone || "",
        isVerified: admin.isVerified,
      });
    } else {
      reset({
        name: "",
        email: "",
        phone: "",
        isVerified: false,
      });
    }
  }, [admin, isEditMode, reset]);

  const onSubmit = async (data: AdminFormData) => {
    try {
      if (isEditMode && admin) {
        await updateAdmin({
          id: admin.id,
          data: {
            name: data.name,
            email: data.email,
            phone: data.phone || "",
            role: "ADMIN", // Always set role as ADMIN for admins
          },
        }).unwrap();
        success("Success", "Admin updated successfully");
      } else {
        await createAdmin({
          ...data,
          role: "ADMIN", // Always set role as ADMIN for admins
          phone: data.phone || "",
          password: "changeme", // Set a default or generated password here
        }).unwrap();
        success("Success", "Admin created successfully");
      }
      onClose();
    } catch (err: any) {
      error(
        "Error",
        isEditMode
          ? "Failed to update admin. Please try again."
          : "Failed to create admin. Please try again."
      );
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 flex items-center"
                  >
                    <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                    {isEditMode
                      ? "Edit Administrator"
                      : "Add New Administrator"}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Warning Notice */}
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-6">
                  <div className="flex items-start">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {isEditMode
                          ? "Modifying admin privileges affects system access and security."
                          : "New admin will have full system access and privileges."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Administrator Name *
                    </label>
                    <input
                      type="text"
                      {...register("name")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter admin full name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register("email")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter admin email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Phone Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      {...register("phone")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Enter phone number (optional)"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>

                  {/* Verification Status (Edit Mode Only) */}
                  {isEditMode && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          {...register("isVerified")}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          <Shield className="w-4 h-4 inline mr-2" />
                          Admin Access Verified
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                        Verified admins have full system access
                      </p>
                    </div>
                  )}

                  {/* Admin Privileges Info */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
                      Administrator Privileges Include:
                    </h4>
                    <ul className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
                      <li>• Full user management access</li>
                      <li>• Property and listing oversight</li>
                      <li>• System settings configuration</li>
                      <li>• Reports and analytics access</li>
                      <li>• Content moderation tools</li>
                    </ul>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      onClick={handleClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      disabled={isLoading || isSubmitting}
                    >
                      {isLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {isLoading
                        ? "Saving..."
                        : isEditMode
                        ? "Update Admin"
                        : "Create Admin"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AdminFormModal;
